import { Error } from 'tslint/lib/error';

import { KeywordsTextTypeView } from '../../components/DisplayConditions/DisplayConditions';
import {
    getEditedBannerCallouts,
    getEditedBannerSiteLinks,
    getEditedBannerVCard,
    getEditedBannerVideo
} from '../../mappers/banners-edit/api-data-mappers';
import {
    EditedBanner,
    EditedBannerBase,
    EditedBannerCampaign,
    EditedBannerGroup,
    EditedBannerTypeCpcVideo,
    EditedBannerTypeCpmAudio,
    EditedBannerTypeCpmBanner,
    EditedBannerTypeCpmGeoproduct,
    EditedBannerTypeCpmIndoor,
    EditedBannerTypeCpmOutdoor,
    EditedBannerTypeCpmVideo,
    EditedBannerTypeImage,
    EditedBannerTypeMcBanner,
    EditedBannerTypePerformance,
    EditedBannerTypeText,
    EditedBannerVideo,
    ProtocolValue
} from '../../typings/bannersEditState';
import {
    AdvType,
    AdvTypeClient,
    ContactsEditorType,
    CopyTooltipStatus
} from '../../typings/domain';
import { CurrencyCode, GdCreativeType } from '../../typings/graphql-grid';
import { Omit } from '../../typings/utils';

import { getDefaultEditedBannerPrice } from './BannerHelpers';

const getNewBaseData = (
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign
): Omit<EditedBannerBase, 'id' | 'bannerType' | 'isNew'> => ({
    isMobile: false,
    isSaved: false,
    isEditAllow: true,
    markedForDeletion: false,
    canBeDeleted: true,
    readOnly: false,
    campaign,
    group,
    errors: undefined,
    status: {
        isPreviousVersionShown: false,
        isRejectedOnModeration: false
    }
});

function makeNewEditedBannerTypeText(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    workCurrency: CurrencyCode,
    initialData: Partial<EditedBannerTypeText>,
    isPromocodeActive: boolean | undefined,
    vCardId: string = '',
    video?: EditedBannerVideo
): EditedBannerTypeText {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType: AdvType.TEXT,
        title: '',
        titleExtension: '',
        body: '',
        dynamicDisclaimer: undefined,
        linkTail: undefined,
        age: '',
        warning: '',
        bannerPriceEditor: getDefaultEditedBannerPrice(workCurrency),
        turboGallery: {
            href: ''
        },
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },
        isPromocodeActive,
        video: video || getEditedBannerVideo(),

        sitelinksSetId: null,
        sitelinks: getEditedBannerSiteLinks(),

        callouts: getEditedBannerCallouts(),

        vCardId,
        vCard: getEditedBannerVCard(vCardId, campaign.id),

        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        },

        ...initialData
    };
}

function makeNewEditedBannerTypeImage(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeImage>,
    isPromocodeActive: boolean | undefined
): EditedBannerTypeImage {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType: AdvType.IMAGE_AD,

        isImageCanvasOpen: false,
        isPromocodeActive,
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        },

        ...initialData
    };
}

function makeNewEditedBannerTypeMcBanner(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeMcBanner>,
    isPromocodeActive: boolean | undefined
): EditedBannerTypeMcBanner {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType: AdvType.MCBANNER,

        isPromocodeActive,
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },

        ...initialData
    };
}

function makeNewEditedBannerTypePerformance(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Pick<EditedBannerTypePerformance, 'creative'>
): EditedBannerTypePerformance {
    const creative = initialData.creative;

    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType: AdvType.PERFORMANCE,
        creative,
        creativeReplaced: false
    };
}

function makeNewEditedBannerTypeCpcVideo(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeCpcVideo>,
    isPromocodeActive: boolean | undefined
): EditedBannerTypeCpcVideo {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType: AdvType.CPC_VIDEO,
        video: getEditedBannerVideo(),
        isPromocodeActive,

        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },

        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        },

        ...initialData
    };
}

function makeNewEditedBannerTypeCpmBanner(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeCpmBanner>,
    isPromocodeActive: boolean | undefined,
    bannerType: AdvType.CPM_BANNER
): EditedBannerTypeCpmBanner {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType,
        isPromocodeActive,
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },
        pixel: '',
        measurers: [],
        tnsId: '',
        isImageCanvasOpen: false,
        showsCounters: [''],
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        creative: {
            data: {
                id: '',
                name: '',
                previewUrl: '',
                width: 0,
                height: 0,
                type: GdCreativeType.CANVAS,
                isAdaptive: false,
                isImage: false
            }
        },
        ...initialData
    };
}

function makeNewEditedBannerTypeCpmOutdoor(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeCpmOutdoor>,
    isPromocodeActive: boolean | undefined,
    bannerType: AdvType.CPM_OUTDOOR
): EditedBannerTypeCpmOutdoor {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType,
        isPromocodeActive,
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },
        pixel: '',
        measurers: [],
        isImageCanvasOpen: false,
        showsCounters: [''],
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        operators: [],
        creative: {
            data: {
                id: '',
                name: '',
                previewUrl: '',
                width: 0,
                height: 0,
                type: GdCreativeType.CANVAS,
                isAdaptive: false,
                isImage: false
            }
        },
        ...initialData
    };
}

function makeNewEditedBannerTypeCpmIndoor(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeCpmIndoor>,
    isPromocodeActive: boolean | undefined,
    bannerType: AdvType.CPM_INDOOR
): EditedBannerTypeCpmIndoor {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType,
        isPromocodeActive,
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },
        pixel: '',
        measurers: [],
        isImageCanvasOpen: false,
        showsCounters: [''],
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        creative: {
            data: {
                id: '',
                name: '',
                previewUrl: '',
                width: 0,
                height: 0,
                type: GdCreativeType.CANVAS,
                isAdaptive: false,
                isImage: false
            }
        },
        ...initialData
    };
}

function makeNewEditedBannerTypeCpmVideo(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeCpmVideo>,
    isPromocodeActive: boolean | undefined,
    bannerType: AdvType.CPM_VIDEO
): EditedBannerTypeCpmVideo {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType,
        isPromocodeActive,
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },
        pixel: '',
        measurers: [],
        tnsId: '',
        isImageCanvasOpen: false,
        showsCounters: [''],
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        creative: {
            data: {
                id: '',
                name: '',
                previewUrl: '',
                width: 0,
                height: 0,
                type: GdCreativeType.CANVAS,
                isAdaptive: false,
                isImage: false
            }
        },
        ...initialData
    };
}

function makeNewEditedBannerTypeCpmGeoproduct(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeCpmGeoproduct>,
    isPromocodeActive: boolean | undefined,
    bannerType: AdvType.CPM_GEOPRODUCT
): EditedBannerTypeCpmGeoproduct {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType,
        isPromocodeActive,
        contacts: {
            turbolanding: undefined,
            mobileType: ContactsEditorType.TURBO
        },
        pixel: '',
        measurers: [],
        isImageCanvasOpen: false,
        showsCounters: [''],
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        creative: {
            data: {
                id: '',
                name: '',
                previewUrl: '',
                width: 0,
                height: 0,
                type: GdCreativeType.CANVAS,
                isAdaptive: false,
                isImage: false
            }
        },
        ...initialData
    };
}

function makeNewEditedBannerTypeCpmAudio(
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    initialData: Partial<EditedBannerTypeCpmAudio>,
    isPromocodeActive: boolean | undefined,
    bannerType: AdvType.CPM_AUDIO
): EditedBannerTypeCpmAudio {
    return {
        id,
        ...getNewBaseData(group, campaign),
        isNew,
        bannerType,
        isPromocodeActive,
        contacts: {
            href: '',
            domain: '',
            displayHref: '',
            isUrlChecking: false,
            turbolanding: undefined,
            hrefProtocol: ProtocolValue.https,
            mobileType: ContactsEditorType.LINK,
            desktopAndTabletType: ContactsEditorType.LINK
        },
        pixel: '',
        measurers: [],
        isImageCanvasOpen: false,
        showsCounters: [''],
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },

        creative: {
            data: {
                id: '',
                name: '',
                previewUrl: '',
                width: 0,
                height: 0,
                type: GdCreativeType.CANVAS,
                isAdaptive: false,
                isImage: false
            }
        },
        ...initialData
    };
}

export function makeNewEditedBanner(
    bannerType: AdvType | AdvTypeClient,
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedBannerCampaign,
    workCurrency: CurrencyCode,
    isPromocodeActive: boolean | undefined,
    initialData: Partial<Omit<EditedBanner, 'bannerType'>> = {},
    vCardId?: string,
    video?: EditedBannerVideo
): EditedBanner {
    switch (bannerType) {
        case AdvType.TEXT:
            return makeNewEditedBannerTypeText(
                id,
                isNew,
                group,
                campaign,
                workCurrency,
                initialData,
                isPromocodeActive,
                vCardId,
                video
            );
        case AdvTypeClient.TEXT_MOBILE:
            return makeNewEditedBannerTypeText(
                id,
                isNew,
                group,
                campaign,
                workCurrency,
                initialData,
                isPromocodeActive,
                vCardId,
                video
            );
        case AdvType.IMAGE_AD:
            return makeNewEditedBannerTypeImage(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive
            );
        case AdvType.CPC_VIDEO:
            return makeNewEditedBannerTypeCpcVideo(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive
            );
        case AdvType.CPM_VIDEO:
            return makeNewEditedBannerTypeCpmVideo(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive,
                bannerType
            );
        case AdvType.CPM_AUDIO:
            return makeNewEditedBannerTypeCpmAudio(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive,
                bannerType
            );
        case AdvType.CPM_BANNER:
            return makeNewEditedBannerTypeCpmBanner(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive,
                bannerType
            );

        case AdvType.CPM_GEOPRODUCT:
            return makeNewEditedBannerTypeCpmGeoproduct(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive,
                bannerType
            );
        case AdvType.CPM_OUTDOOR:
            return makeNewEditedBannerTypeCpmOutdoor(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive,
                bannerType
            );
        case AdvType.CPM_INDOOR:
            return makeNewEditedBannerTypeCpmIndoor(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive,
                bannerType
            );
        case AdvType.MCBANNER:
            return makeNewEditedBannerTypeMcBanner(
                id,
                isNew,
                group,
                campaign,
                initialData,
                isPromocodeActive
            );
        default:
            throw new Error(`неподдерживаемый тип объявления ${bannerType}`);
    }
}

export function makeNewPerformanceEditedBanner(
    id: string,
    group: EditedBannerGroup,
    isNew: boolean,
    campaign: EditedBannerCampaign,
    initialData: Pick<EditedBannerTypePerformance, 'creative'>
): EditedBannerTypePerformance {
    return makeNewEditedBannerTypePerformance(
        id,
        isNew,
        group,
        campaign,
        initialData
    );
}
